import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { useField, useFormikContext } from 'formik'

import SelectBox, { SelectBoxRef, FieldAddons } from 'devextreme-react/select-box'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { useTranslation } from 'react-i18next'
import { useLabelWidth } from 'hooks/use-label-width'

import { getIconClass } from 'helpers/get-icon-class'

import './index.scss'
import { FormErrors } from 'components/forms/form-errors'
import useResizeObserver from '@react-hook/resize-observer'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'


export const SelectInput = ({label, opts, readOnly = false, valueExpr = 'value', displayExpr = 'name', showDataBeforeSearch = true, minSearchLength = 3, searchEnabled = true, ...props}: any) => {
    const { setFieldValue } = useFormikContext()
    const [field, meta] = useField(props)
    const { t } = useTranslation()
    const selectBoxRef = useRef<SelectBoxRef>(null)
    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const [inFocus, setInFocus] = useState(false)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const getLabelWidth = useLabelWidth(formFieldType)
    const [open, setOpen] = useState(false)

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((selectBoxRef.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        if (isFloatingLabel) {

            const selectBox: any = selectBoxRef.current
            const floatingLabel: HTMLElement = selectBox?.instance()?.element().querySelector('.dx-label')

            if (floatingLabel) {
                const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                const floatingLabelWidth = getLabelWidth(label, fontFamily)
                const input: any = selectBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+15)}px`)
            }

            if (field.value || field.value === 0) {
                selectBox?.instance()?.element().classList.add('input-has-value')

                const focusedFloatingLabel: HTMLElement = selectBox?.instance()?.element().querySelector('.dx-texteditor-label .dx-label')

                floatingLabel && focusedFloatingLabel && (focusedFloatingLabel.style.left = `calc(100% - 40px - ${floatingLabel.offsetWidth-20}px)`)
            }
            else {
                selectBox?.instance()?.element().classList.remove('input-has-value')

                if (floatingLabel) {
                    floatingLabel.style.left = inFocus ? `calc(100% - 40px - ${floatingLabel.offsetWidth-20}px)` : '0px'
                }
            }
        }

    },[field.value, getLabelWidth, label, fieldWidth, inFocus, isFloatingLabel])

    const handleChange = (e: any) => {
        setFieldValue(field.name, e.value, true)
    }

    const handleBlur = (e: any) => {
        field.onBlur(e.event)
        setInFocus(false)
    }

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const optsValues = useMemo(() => opts.map((x: any) => x.value), [opts])

    useEffect(() => {
        if (field.value === null) { return }
        if (!optsValues.includes(field.value)) {
            setFieldValue(field.name, null)
        }
    }, [optsValues, field.value, field.name, setFieldValue])

    const ddButtons = useMemo<any>(() => ([
        { name: 'dropDown' },

        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }
        }
    ]), [label])

    const onOptionChanged = useCallback((e: any) => {
        if (e.name === 'opened') {
            setOpen(e.value)
        }
    }, [])

    const ddOptions = useMemo(() => ({
        wrapperAttr: { class: 'ix-select-input-wrapper' }
    }), [])

    const beforeRender = useCallback((value: any) => {
        if (!value || !value.icon) { return null }
        const iconDef = value.icon

        return (
            <div className='ix-input-icon-container'>
                <i className={`ix-input-icon ${getIconClass(iconDef.icon)}` }style={ iconDef.color ? { color: iconDef.color } : {} } />
            </div>
        )
    }, [])

    const itemRender = useCallback((item: any) => {
        if (item.icon) {
            const iconDef = item.icon
            return (
                <div className='ix-select-input-item'>
                    <div className='ix-input-icon-container'>
                        <i className={`ix-input-icon ${getIconClass(iconDef.icon)}` }style={ iconDef.color ? { color: iconDef.color } : {} } />
                    </div>
                    <div>{ item[displayExpr] }</div>
                </div>
            )
        } else {
            return (
                <div className='ix-select-input-item'>
                    <div>{ item[displayExpr] }</div>
                </div>
            )
        }
    }, [displayExpr])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label htmlFor={props.id || field.name} className='dx-field-label'>{label}</label>
                <SelectBox
                    ref={selectBoxRef}
                    label={label}
                    labelMode= 'floating'
                    buttons={ddButtons}
                    dataSource={opts}
                    name={field.name}
                    inputAttr={{name: field.name}}
                    value={field.value}
                    onValueChanged={handleChange}
                    onFocusIn={onFocusIn}
                    onFocusOut={handleBlur}
                    onOptionChanged={ onOptionChanged }
                    opened={ open }
                    className='dx-field-input'
                    searchEnabled={searchEnabled}
                    showDataBeforeSearch={showDataBeforeSearch}
                    minSearchLength={minSearchLength}
                    acceptCustomValue={false}
                    isValid={!(meta.touched && meta.error)}
                    placeholder={t('global:label.select')}
                    valueExpr={valueExpr}
                    displayExpr={displayExpr}
                    readOnly={readOnly}
                    height={26}
                    dropDownOptions={ddOptions}
                    itemRender={ itemRender }
                >
                    <FieldAddons beforeRender={ beforeRender } />
                </SelectBox>
            </div>
            <FormErrors meta={meta} />
        </div>
    )
}
